/**
 * Client half of the idle-session policy (shared/sessionPolicy.ts).
 *
 * Three jobs, all of them cross-tab:
 *
 *  1. Remember when the user last actually did something. In `localStorage`,
 *     not a module variable, because a sales office runs the console in three
 *     tabs and working in one of them must not let the other two decide the
 *     user has gone home.
 *  2. Tell the server about it, at most once every few minutes, from whichever
 *     tab gets there first.
 *  3. Carry the REASON for a sign-out to the login page, so it can say what
 *     happened rather than looking like an ordinary logged-out screen.
 *
 * Every access is wrapped: `localStorage` throws outright in some privacy
 * modes, and an idle timer that crashes the app is worse than no idle timer.
 * When storage is unavailable each tab simply falls back to its own in-memory
 * clock — the server is enforcing the real thing either way.
 */

import { ACTIVITY_PING_INTERVAL_MS } from '@shared/sessionPolicy';
import { apiRequest } from './queryClient';

const LAST_ACTIVITY_KEY = 'salefish:session:lastActivity';
const LAST_PING_KEY = 'salefish:session:lastPing';
/** Written to localStorage purely to fire a `storage` event in sibling tabs. */
export const SIGNED_OUT_BROADCAST_KEY = 'salefish:session:signedOutAt';
/** Per-tab, and it must be: it is consumed once, by the login page that renders next. */
const SIGNED_OUT_REASON_KEY = 'salefish:session:signedOutReason';

export type SignedOutReason = 'idle' | 'expired';

/** In-memory mirror, used when localStorage is unavailable or unreadable. */
let memoryActivity = Date.now();
let memoryPing = 0;

function readNumber(key: string, fallback: number): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const value = Number(raw);
    // A NaN or a timestamp from the future (clock change, corrupted value) is
    // not evidence the user is active — but it must not read as "idle for
    // 56 years" either, which would sign them out on the next tick.
    if (!Number.isFinite(value) || value > Date.now()) return fallback;
    return value;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* private mode / storage disabled — the in-memory mirror carries it */
  }
}

/** ms epoch of the last real user interaction, across every tab. */
export function readLastActivity(): number {
  return readNumber(LAST_ACTIVITY_KEY, memoryActivity);
}

/** Record real user interaction. Cheap enough to call from a throttled listener. */
export function markActivity(now: number = Date.now()): void {
  memoryActivity = now;
  writeNumber(LAST_ACTIVITY_KEY, now);
}

/**
 * Send the activity ping if one is due.
 *
 * `lastPing` is claimed BEFORE the request goes out, so three tabs ticking at
 * the same moment send one ping between them rather than three. A failed ping
 * is not retried immediately — with a 3-minute cadence inside a 30-minute
 * window there are nine more attempts before it could matter.
 */
export function pingIfDue(idleForMs: number): void {
  const now = Date.now();
  const lastPing = readNumber(LAST_PING_KEY, memoryPing);

  // Nothing has happened since the last ping — let the clock run.
  if (now - idleForMs <= lastPing) return;
  if (now - lastPing < ACTIVITY_PING_INTERVAL_MS) return;

  memoryPing = now;
  writeNumber(LAST_PING_KEY, now);

  // `stepUp: 'passive'`: nobody pressed anything, so a session owing its
  // second factor must not re-open a prompt the user just dismissed. A 401
  // still routes to login — that is the ping doing its job.
  apiRequest('POST', '/api/auth/activity', {
    idleForSeconds: Math.round(idleForMs / 1000),
  }, { stepUp: 'passive' }).catch(() => {
    /* Offline or already expired. apiRequest handles a 401 by routing to login. */
  });
}

/** Start the clocks fresh — on sign-in, and on sign-out so nothing leaks to the next user. */
export function resetSessionActivity(): void {
  memoryActivity = Date.now();
  memoryPing = 0;
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_KEY);
    window.localStorage.removeItem(LAST_PING_KEY);
    window.localStorage.removeItem(SIGNED_OUT_BROADCAST_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** Tell the other tabs to sign out too. Fires their `storage` listeners. */
export function broadcastSignedOut(): void {
  writeNumber(SIGNED_OUT_BROADCAST_KEY, Date.now());
}

/**
 * Leave a note for the login page. sessionStorage, so it survives the
 * `window.location.replace('/api/logout')` full navigation that `redirectToLogin`
 * uses, without surviving into a different tab or a later visit.
 */
export function recordSignedOutReason(reason: SignedOutReason): void {
  try {
    window.sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
  } catch {
    /* no note; the login page just renders normally */
  }
}

/** Read and clear the note — a reload must not toast the same sign-out twice. */
export function takeSignedOutReason(): SignedOutReason | null {
  try {
    const reason = window.sessionStorage.getItem(SIGNED_OUT_REASON_KEY);
    if (reason !== 'idle' && reason !== 'expired') return null;
    window.sessionStorage.removeItem(SIGNED_OUT_REASON_KEY);
    return reason;
  } catch {
    return null;
  }
}
